import React, { useEffect, useState } from 'react';
import {
  exportCatalogUrl,
  fetchAdminCatalog,
  publishCatalog,
  resetCatalogDraft,
  rollbackCatalog,
  saveCatalogDraft,
  validateCatalog,
} from './api';
import { CoverageMatrixView } from './components/CoverageMatrixView';
import { CoverageReviewQuestionsTab } from './components/CoverageReviewQuestionsTab';
import { CoverageRoleAccessTab } from './components/CoverageRoleAccessTab';
import { PublishConfirmModal } from './components/PublishConfirmModal';
import { ValidationIssuesModal } from './components/ValidationIssuesModal';
import { VersionHistoryModal } from './components/VersionHistoryModal';
import './cov178.css';
import './coverage.css';
import './service-coverage.css';
import type {
  CatalogAdminResponse,
  CatalogAIPolicy,
  CatalogPreliminary,
  CatalogQuestion,
  CatalogValidationIssue,
} from './types';

type CoverageCatalogPageProps = {
  onClose?: () => void;
  onBack?: () => void;
};

export const CoverageCatalogPage: React.FC<CoverageCatalogPageProps> = ({ onClose, onBack }) => {
  const [data, setData] = useState<CatalogAdminResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'home' | 'matrix' | 'access' | 'review'>('home');
  const [targetReviewServiceId, setTargetReviewServiceId] = useState<string | undefined>(undefined);

  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [validationIssues, setValidationIssues] = useState<CatalogValidationIssue[]>([]);
  const [feedbackMessage, setFeedbackMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  const loadCatalog = () => {
    setLoading(true);
    fetchAdminCatalog()
      .then((res) => {
        setData(res);
        setValidationIssues(res.validation_issues);
      })
      .catch(() => {
        setFeedbackMessage({ text: 'Не удалось загрузить каталог', type: 'error' });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCatalog();
  }, []);

  useEffect(() => {
    document.body.classList.add('cov178-open');
    return () => {
      document.body.classList.remove('cov178-open');
    };
  }, []);

  const goHome = () => setActiveTab('home');
  const handleBack = onBack || onClose;

  const handleToggleServiceAccess = (roleCode: string, serviceId: string, currentAccess: string) => {
    const nextAccess = currentAccess === 'ALLOWED' ? 'DENIED' : 'ALLOWED';
    saveCatalogDraft({
      role_access: [
        {
          role_code: roleCode,
          service_id: serviceId,
          access: nextAccess,
          source: 'OWNER_OVERRIDE',
        },
      ],
    }).then((res) => {
      setData(res);
      setFeedbackMessage({ text: 'Изменение доступа сохранено в черновик', type: 'info' });
    });
  };

  const handleAllowAllDirection = (roleCode: string, directionId: string) => {
    if (!data) return;
    const dirServices = data.services.filter((s) => s.primary_direction_id === directionId);
    const updates = dirServices.map((s) => ({
      role_code: roleCode,
      service_id: s.service_id,
      access: 'ALLOWED',
      source: 'OWNER_OVERRIDE',
    }));
    saveCatalogDraft({
      role_access: [
        ...updates,
        {
          role_code: roleCode,
          direction_id: directionId,
          service_id: null,
          access: 'ALLOWED',
          source: 'OWNER_OVERRIDE',
        },
      ],
    }).then((res) => {
      setData(res);
      setFeedbackMessage({ text: 'Все услуги направления разрешены в черновике', type: 'success' });
    });
  };

  const handleDenyAllDirection = (roleCode: string, directionId: string) => {
    if (!data) return;
    const dirServices = data.services.filter((s) => s.primary_direction_id === directionId);
    const updates = dirServices.map((s) => ({
      role_code: roleCode,
      service_id: s.service_id,
      access: 'DENIED',
      source: 'OWNER_OVERRIDE',
    }));
    saveCatalogDraft({
      role_access: [
        ...updates,
        {
          role_code: roleCode,
          direction_id: directionId,
          service_id: null,
          access: 'DENIED',
          source: 'OWNER_OVERRIDE',
        },
      ],
    }).then((res) => {
      setData(res);
      setFeedbackMessage({ text: 'Направление запрещено в черновике', type: 'info' });
    });
  };

  const handleResetDirectionPolicy = (roleCode: string, directionId: string) => {
    handleAllowAllDirection(roleCode, directionId);
  };

  const handleUpdateClientMapping = (clientCategoryId: string, targetServiceId: string) => {
    if (!data) return;
    const existing = data.client_mappings.find((cm) => cm.client_category_id === clientCategoryId);
    if (!existing) return;
    saveCatalogDraft({
      client_mappings: [
        {
          client_category_id: clientCategoryId,
          category_title: existing.category_title,
          service_id: targetServiceId,
          routing_rule: existing.routing_rule,
          order: existing.order,
        },
      ],
    }).then((res) => {
      setData(res);
      setFeedbackMessage({ text: 'Маппинг категории клиента обновлён', type: 'success' });
    });
  };

  const handleUpdateQuestion = (updated: Partial<CatalogQuestion>) => {
    if (!updated.question_id || !updated.service_id) return;
    saveCatalogDraft({
      questions: [
        {
          question_id: updated.question_id,
          service_id: updated.service_id,
          stage: updated.stage ?? 2,
          block: updated.block ?? 'A',
          label: updated.label || '',
          field_type: updated.field_type || 'text',
          placeholder: updated.placeholder,
          help_text: updated.help_text,
          options: updated.options || [],
          required: updated.required ?? false,
          required_when_visible: updated.required_when_visible ?? false,
          show_if: updated.show_if,
          required_if: updated.required_if,
          prefill_policy: updated.prefill_policy,
          order: updated.order ?? 1,
          status: 'CHANGED',
          is_deleted: updated.is_deleted ?? false,
        },
      ],
    }).then((res) => {
      setData(res);
      setFeedbackMessage({ text: 'Вопрос обновлён в черновике', type: 'info' });
    });
  };

  const handleToggleQuestionApproval = (questionId: string, currentStatus: string) => {
    const q = data?.questions.find((item) => item.question_id === questionId);
    if (!q) return;
    const nextStatus = currentStatus === 'APPROVED' ? 'CHANGED' : 'APPROVED';
    handleUpdateQuestion({
      ...q,
      status: nextStatus as any,
    });
  };

  const handleDeleteQuestion = (questionId: string) => {
    const q = data?.questions.find((item) => item.question_id === questionId);
    if (!q) return;
    handleUpdateQuestion({
      ...q,
      is_deleted: true,
    });
  };

  const handleRestoreQuestion = (questionId: string) => {
    const q = data?.questions.find((item) => item.question_id === questionId);
    if (!q) return;
    handleUpdateQuestion({
      ...q,
      is_deleted: false,
    });
  };

  const handleUpdatePreliminary = (updated: Partial<CatalogPreliminary>) => {
    if (!updated.question_id || !updated.service_id) return;
    saveCatalogDraft({
      preliminaries: [
        {
          question_id: updated.question_id,
          service_id: updated.service_id,
          value_template: updated.value_template || '',
          status: updated.status || 'NEEDS_CONFIRMATION',
          source_type: updated.source_type || 'SYSTEM_PREFILL',
          confidence: updated.confidence,
          source_ref: updated.source_ref,
          confirmation_required: updated.confirmation_required ?? true,
          notes: updated.notes,
          status_review: 'CHANGED',
          is_deleted: updated.is_deleted ?? false,
        },
      ],
    }).then((res) => {
      setData(res);
      setFeedbackMessage({ text: 'Предварительный ответ обновлён в черновике', type: 'info' });
    });
  };

  const handleTogglePreliminaryApproval = (questionId: string, currentStatus: string) => {
    const p = data?.preliminaries.find((item) => item.question_id === questionId);
    if (!p) return;
    const nextStatus = currentStatus === 'APPROVED' ? 'CHANGED' : 'APPROVED';
    handleUpdatePreliminary({
      ...p,
      status_review: nextStatus as any,
    });
  };

  const handleUpdateAIPolicy = (policy: CatalogAIPolicy) => {
    saveCatalogDraft({
      ai_policy: policy,
    }).then((res) => {
      setData(res);
      setFeedbackMessage({ text: 'Политика консультанта обновлена в черновике', type: 'info' });
    });
  };

  const handleRunValidation = () => {
    validateCatalog().then((res) => {
      setValidationIssues(res.issues);
      setIsValidationModalOpen(true);
    });
  };

  const handlePublish = (comment: string) => {
    publishCatalog(comment)
      .then((res) => {
        setData(res);
        setFeedbackMessage({ text: `Версия v${res.active_version} успешно опубликована`, type: 'success' });
      })
      .catch((err) => {
        setFeedbackMessage({ text: err?.message || 'Ошибка публикации', type: 'error' });
      });
  };

  const handleResetDraft = () => {
    if (window.confirm('Сбросить все непопубликованные черновики к текущей опубликованной версии?')) {
      resetCatalogDraft().then((res) => {
        setData(res);
        setFeedbackMessage({ text: 'Локальные правки сброшены', type: 'info' });
      });
    }
  };

  const handleRollback = (targetVer: number, comment: string) => {
    rollbackCatalog(targetVer, comment).then((res) => {
      setData(res);
      setFeedbackMessage({ text: `Каталог успешно откачен к состоянию версии v${targetVer}`, type: 'success' });
    });
  };

  const navigateToServiceReview = (srvId: string) => {
    setTargetReviewServiceId(srvId);
    setActiveTab('review');
  };

  const navigateToRoleAccess = () => {
    setActiveTab('access');
  };

  if (loading && !data) {
    return (
      <div className="cov178-overlay" role="dialog" aria-modal="true" aria-label="Покрытие услуг 11×72">
        <header className="cov178-top">
          {handleBack ? (
            <button type="button" className="cov178-b" onClick={handleBack}>
              ← Назад
            </button>
          ) : null}
          <div>
            <strong>Покрытие услуг · 11×72</strong>
            <small>Доступные услуги + вопросы и предварительные ответы</small>
          </div>
          <div className="cov178-sp" />
          {onClose ? (
            <button type="button" className="cov178-b" onClick={onClose}>
              Закрыть ×
            </button>
          ) : null}
        </header>
        <main className="cov178-main">
          <div className="cov178-loading">Загрузка покрытия услуг 11×72…</div>
        </main>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="cov178-overlay" role="dialog" aria-modal="true" aria-label="Покрытие услуг 11×72">
      <header className="cov178-top">
        <button
          type="button"
          className="cov178-b"
          onClick={() => {
            if (activeTab === 'home') handleBack?.();
            else goHome();
          }}
        >
          ← Назад
        </button>
        <div>
          <strong>Покрытие услуг · 11×72</strong>
          <small>Доступные услуги + вопросы и предварительные ответы</small>
        </div>
        <div className="cov178-sp" />
        <button
          type="button"
          className={`cov178-b${activeTab === 'access' ? ' active' : ''}`}
          onClick={() => setActiveTab('access')}
        >
          Доступные услуги
        </button>
        <button
          type="button"
          className={`cov178-b${activeTab === 'review' ? ' active' : ''}`}
          onClick={() => setActiveTab('review')}
        >
          Вопросы и предварительные ответы
        </button>
        <button
          type="button"
          className={`cov178-b${activeTab === 'matrix' ? ' active' : ''}`}
          onClick={() => setActiveTab('matrix')}
        >
          Матрица покрытия
        </button>
        {onClose ? (
          <button type="button" className="cov178-b" onClick={onClose}>
            Закрыть ×
          </button>
        ) : null}
      </header>

      <main className="cov178-main">
        {activeTab === 'home' ? (
          <div className="cov178-home">
            <button
              type="button"
              className="cov178-card"
              style={{ ['--a' as string]: '#41d2ff' }}
              onClick={() => setActiveTab('access')}
              aria-label="Открыть доступные услуги по категориям"
            >
              <b>01</b>
              <strong>Доступные услуги по категориям</strong>
              <p>
                Канонический Owner Review: направление/услуга, утвердить, удалить, восстановить,
                предпросмотр «Создать заявку» и журнал изменений. Управляет фактическим доступом ролей.
              </p>
              <span>Открыть редактор →</span>
            </button>
            <button
              type="button"
              className="cov178-card"
              style={{ ['--a' as string]: '#49ddb7' }}
              onClick={() => setActiveTab('review')}
              aria-label="Открыть вопросы и предварительные ответы"
            >
              <b>02</b>
              <strong>Вопросы и предварительные ответы</strong>
              <p>
                Канонический Owner Review: 11 направлений, 72 услуги, A/B/C, редактирование вопроса и
                предварительного ответа, утверждение и soft-delete.
              </p>
              <span>Открыть редактор →</span>
            </button>
          </div>
        ) : (
          <div className="cov178-body">
            <div className="cov178-toolbar">
              <span className="cov178-badge">v{data.active_version}</span>
              <div className="cov178-toolbar-sp" />
              <button type="button" className="cov-btn cov-btn-outline" onClick={handleRunValidation}>
                Проверить изменения
              </button>
              <button
                type="button"
                className="cov-btn cov-btn-primary"
                disabled={!data.has_draft_changes}
                onClick={() => setIsPublishModalOpen(true)}
              >
                Опубликовать
              </button>
              <button
                type="button"
                className="cov-btn cov-btn-outline"
                onClick={() => setIsHistoryModalOpen(true)}
              >
                История версий
              </button>
              <button
                type="button"
                className="cov-btn cov-btn-danger-outline"
                disabled={!data.has_draft_changes}
                onClick={handleResetDraft}
              >
                Сбросить черновик
              </button>
              <a
                href={exportCatalogUrl}
                className="cov-btn cov-btn-outline"
                download="coverage_catalog_11x72.json"
              >
                Экспорт каталога
              </a>
            </div>

            {feedbackMessage ? (
              <div
                className={`cov178-feedback ${
                  feedbackMessage.type === 'success'
                    ? 'ok'
                    : feedbackMessage.type === 'error'
                      ? 'err'
                      : 'info'
                }`}
              >
                <span>{feedbackMessage.text}</span>
                <button type="button" onClick={() => setFeedbackMessage(null)}>
                  ✕
                </button>
              </div>
            ) : null}

            {activeTab === 'matrix' ? (
              <CoverageMatrixView
                data={data}
                onOpenQuestions={navigateToServiceReview}
                onOpenAccess={navigateToRoleAccess}
                onExport={() => {
                  window.open(exportCatalogUrl, '_blank', 'noopener,noreferrer');
                }}
              />
            ) : activeTab === 'access' ? (
              <CoverageRoleAccessTab
                directions={data.directions}
                services={data.services}
                roleAccess={data.role_access}
                clientMappings={data.client_mappings}
                onToggleServiceAccess={handleToggleServiceAccess}
                onAllowAllDirection={handleAllowAllDirection}
                onDenyAllDirection={handleDenyAllDirection}
                onResetDirectionPolicy={handleResetDirectionPolicy}
                onUpdateClientMapping={handleUpdateClientMapping}
                onNavigateToServiceReview={navigateToServiceReview}
              />
            ) : (
              <CoverageReviewQuestionsTab
                directions={data.directions}
                services={data.services}
                questions={data.questions}
                preliminaries={data.preliminaries}
                aiPolicy={data.ai_policy}
                initialSelectedServiceId={targetReviewServiceId}
                onUpdateQuestion={handleUpdateQuestion}
                onToggleQuestionApproval={handleToggleQuestionApproval}
                onDeleteQuestion={handleDeleteQuestion}
                onRestoreQuestion={handleRestoreQuestion}
                onUpdatePreliminary={handleUpdatePreliminary}
                onTogglePreliminaryApproval={handleTogglePreliminaryApproval}
                onUpdateAIPolicy={handleUpdateAIPolicy}
                onNavigateToRoleAccess={navigateToRoleAccess}
              />
            )}
          </div>
        )}
      </main>

      <div className="sr-only">
        <button type="button" onClick={() => setActiveTab('access')}>
          Доступы по ролям
        </button>
        <button type="button" onClick={() => setActiveTab('review')}>
          Вопросы и предварительные ответы
        </button>
      </div>

      <ValidationIssuesModal
        issues={validationIssues}
        isOpen={isValidationModalOpen}
        onClose={() => setIsValidationModalOpen(false)}
      />

      <PublishConfirmModal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        onConfirm={handlePublish}
        draftChangesCount={data.kpis.draft_changes_count}
      />

      <VersionHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        onRollback={handleRollback}
      />
    </div>
  );
};

export default CoverageCatalogPage;
